// Route guard. Redirects unauthenticated users to /login and covers the
// initial auth-loading window with an optimistic app skeleton (or, for likely
// signed-out visitors, the neutral brand splash).
//
// For a returning user that window is normally gone: AuthProvider starts from
// the session Firebase Auth persisted on this device (lib/persistedSession.ts),
// so `loading` is false on the first render and the real shell mounts at once.
// The skeleton below remains the fallback when a persisted entry exists but
// cannot be used (unparseable, or its token undecodable) — then we wait for
// Firebase Auth exactly as before.

import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';

import { useAuth } from '../lib/auth';
import { BrandSplash } from './BrandSplash';
import { AppShellSkeleton } from './AppShellSkeleton';

// Heuristic: is a persisted Firebase auth session present? The Web SDK stores
// the signed-in user under a `firebase:authUser:*` localStorage key (we use
// browserLocalPersistence — see lib/firebase.ts). When present, the visitor is
// almost certainly about to be re-authenticated, so we show the optimistic app
// skeleton during the brief claims-loading window (IMP-046 P2) — the app's
// shape appears immediately. When absent, the visitor is likely signed out and
// headed to /login, so we keep the neutral BrandSplash rather than flash a fake
// app shell at them. This is UX-only: the real auth decision still comes from
// onIdTokenChanged in AuthProvider, reflected in `user`/`loading` below.
function hasPersistedSession(): boolean {
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      if (localStorage.key(i)?.startsWith('firebase:authUser:')) return true;
    }
  } catch {
    // localStorage can throw in some privacy modes — fall back to the splash.
  }
  return false;
}

export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return hasPersistedSession() ? <AppShellSkeleton /> : <BrandSplash />;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;

  return <>{children}</>;
}
